// tokenStore.js — single audit point for auth-token access.
//
// Why this exists:
//   The app stores the JWT in localStorage so the user stays logged in across
//   browser restarts (the standard SPA pattern). localStorage is readable by
//   any JavaScript on the page, so an XSS bug could leak the token. Moving to
//   httpOnly cookies would close that hole but requires CSRF tokens, CORS
//   credentials=true, and would force-log-out the existing 1-lakh+ live users.
//
//   Until we can stage a proper cookie migration, this module gives us:
//     1) Exactly ONE place that reads/writes the token in the codebase.
//     2) An in-memory cache so most reads after boot don't hit localStorage,
//        shrinking the XSS attack window.
//     3) A clean swap-point for the future cookie migration — change this
//        file alone, and every caller is upgraded.

let _tokenCache = null;
let _adminCache = null;

const TOKEN_KEY = 'token';
const ADMIN_KEY = 'isAdmin';

export const getToken = () => {
  if (_tokenCache !== null) return _tokenCache;
  try {
    _tokenCache = localStorage.getItem(TOKEN_KEY);
  } catch {
    _tokenCache = null;
  }
  return _tokenCache;
};

export const setToken = (token) => {
  _tokenCache = token;
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch (e) {
    console.warn('tokenStore: localStorage unavailable', e);
  }
};

export const getIsAdmin = () => {
  if (_adminCache !== null) return _adminCache;
  try {
    _adminCache = localStorage.getItem(ADMIN_KEY) === 'true';
  } catch {
    _adminCache = false;
  }
  return _adminCache;
};

export const setIsAdmin = (flag) => {
  _adminCache = !!flag;
  try {
    if (flag) localStorage.setItem(ADMIN_KEY, 'true');
    else localStorage.removeItem(ADMIN_KEY);
  } catch (e) {
    console.warn('tokenStore: localStorage unavailable', e);
  }
};

export const clearAuth = () => {
  _tokenCache = null;
  _adminCache = null;
  try {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(ADMIN_KEY);
  } catch (e) { /* best effort */ }
};
